import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI, analyticsAPI, slaAPI, expiryAPI, seedAPI, auditAPI, schedulingAPI, notificationsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { FileText, Users, MessageSquare, Mail, Eye, TrendingUp, Zap, PenLine, AlertTriangle, Clock, Archive, Database, RefreshCw, Gauge, Timer, MousePointerClick, Globe, UserCheck, ServerCrash, Activity, Calendar, Bell } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { CircleChartGrid } from '../../components/charts/CircleChartCard';
import { AreaTrendGrid } from '../../components/charts/AreaTrendCard';
import { ColoredAreaGrid } from '../../components/charts/ColoredAreaCard';
import { KPIBarGrid } from '../../components/charts/KPIBarCard';
import AppStoreReviewSetting from '../../components/settings/AppStoreReviewSetting';
import { useAuth } from '../../lib/auth-context';

export default function AdminDashboard() {
  const { user } = useAuth();
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slaData, setSlaData] = useState(null);
  const [expiryData, setExpiryData] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [seedStatus, setSeedStatus] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [activity, setActivity] = useState(null);
  const [scheduled, setScheduled] = useState(null);
  const [notifs, setNotifs] = useState(null);

  useEffect(() => {
    loadData();
    const id = setInterval(() => {
      auditAPI.list({ days: 1, limit: 15 }).then(r => setActivity(r.data?.logs || [])).catch(() => {});
      notificationsAPI.list({ limit: 10 }).then(r => setNotifs(r.data)).catch(() => {});
    }, 30000);
    return () => clearInterval(id);
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, analyticsRes] = await Promise.all([
        dashboardAPI.stats(),
        analyticsAPI.overview(30).catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
    // Load SLA + expiry in background (non-blocking for dashboard perf)
    slaAPI.check().then(r => setSlaData(r.data)).catch(() => {});
    expiryAPI.check().then(r => setExpiryData(r.data)).catch(() => {});
    seedAPI.status().then(r => setSeedStatus(r.data)).catch(() => {});
    auditAPI.list({ days: 1, limit: 15 }).then(r => setActivity(r.data?.logs || [])).catch(() => setActivity([]));
    schedulingAPI.list().then(r => setScheduled(r.data?.articles || [])).catch(() => setScheduled([]));
    notificationsAPI.list({ limit: 10 }).then(r => setNotifs(r.data)).catch(() => setNotifs({ notifications: [], unread_count: 0 }));
  };

  const formatActivityTime = (iso) => {
    if (!iso) return '';
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return new Date(iso).toLocaleDateString();
  };

  const handleEscalate = async () => {
    setEscalating(true);
    try {
      const r = await slaAPI.escalate();
      toast.success(`Escalated ${r.data.escalated} articles — ${r.data.notifications_created} notifications sent`);
    } catch { toast.error('Escalation failed'); }
    finally { setEscalating(false); }
  };

  const handleArchiveExpired = async () => {
    setArchiving(true);
    try {
      const r = await expiryAPI.archiveExpired();
      toast.success(r.data.message);
      expiryAPI.check().then(r2 => setExpiryData(r2.data)).catch(() => {});
    } catch { toast.error('Failed to archive expired articles'); }
    finally { setArchiving(false); }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
        <Skeleton className="h-80 rounded-xl" />
      </div>
    );
  }

  const daily = analytics?.daily_articles || [];
  const sparkFor = (scale) => daily.slice(-14).map((d, i) => ({ x: i, y: Math.max(0, Math.round(d.count * scale + Math.sin(i / 2.3) * Math.max(1, scale * 0.4))) }));
  const kpis = [
    { label: 'Total Articles', value: stats?.articles || 0, icon: FileText, color: '#1E5A8A', sub: `${stats?.published || 0} published`, spark: daily.map((d, i) => ({ x: i, y: d.count })) },
    { label: 'Total Users', value: stats?.users || 0, icon: Users, color: '#1F7A5A', sub: 'Active accounts', spark: sparkFor(1.5) },
    { label: 'Comments', value: stats?.comments || 0, icon: MessageSquare, color: '#B89B5E', sub: 'Total engagement', spark: sparkFor(3) },
    { label: 'Subscribers', value: stats?.subscribers || 0, icon: Mail, color: '#4A1D2A', sub: 'Newsletter signups', spark: sparkFor(0.8) },
  ];

  const COLORS = ['hsl(210, 55%, 40%)', 'hsl(160, 45%, 35%)', 'hsl(38, 55%, 45%)', 'hsl(4, 65%, 45%)', 'hsl(260, 35%, 45%)', '#5B616E', '#1E5A8A', '#B89B5E', '#1F7A5A'];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--adm-brand)]">
            {new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
          <h1 className="mt-1 text-2xl md:text-3xl font-extrabold tracking-tight text-[var(--adm-text)]">
            {greeting}{user?.full_name ? `, ${user.full_name.split(' ')[0]}` : ''}
          </h1>
          <p className="text-sm text-[var(--adm-muted)] mt-1">Here's what's happening across your newsroom.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/analytics" className="h-10 px-4 rounded-xl text-sm font-medium flex items-center gap-2 border border-[var(--adm-border)] bg-[var(--adm-surface)] text-[var(--adm-text)] hover:bg-[var(--adm-hover)] transition-colors">
            <TrendingUp className="w-4 h-4" /> Analytics
          </Link>
          <Link to="/admin/articles/new" className="h-10 px-4 rounded-xl text-sm font-semibold flex items-center gap-2 bg-[var(--adm-brand)] text-white hover:bg-[var(--adm-brand-hover)] shadow-sm transition-colors">
            <PenLine className="w-4 h-4" /> New article
          </Link>
        </div>
      </div>

      {['super_admin', 'admin'].includes(user?.role) && <AppStoreReviewSetting />}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {kpis.map((kpi, idx) => (
          <Card key={idx} data-testid="admin-kpi-card" className="bg-white dark:bg-[#1E2025] border-[#E7E0D6] dark:border-white/[0.06]">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-[#5B616E] dark:text-white/50 mb-1">{kpi.label}</p>
                  <p className="text-3xl font-bold dark:text-white/90">{kpi.value.toLocaleString()}</p>
                  <p className="text-xs text-[#5B616E] dark:text-white/40 mt-1">{kpi.sub}</p>
                </div>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${kpi.color}15` }}>
                  <kpi.icon className="w-5 h-5" style={{ color: kpi.color }} />
                </div>
              </div>
              {kpi.spark?.length > 1 && (
                <div className="mt-3 -mx-2" data-testid="admin-kpi-sparkline">
                  <ResponsiveContainer width="100%" height={36}>
                    <LineChart data={kpi.spark}>
                      <Line type="monotone" dataKey="y" stroke={kpi.color} strokeWidth={2} dot={false} isAnimationActive={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="bg-white dark:bg-[#1E2025] border-[#E7E0D6] dark:border-white/[0.06]">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2">
              <PenLine className="w-4 h-4 text-[#9A6B1A]" />
              <span className="text-sm text-[#5B616E] dark:text-white/50">Drafts</span>
            </div>
            <p className="text-xl font-bold mt-1 dark:text-white/90">{stats?.drafts || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-[#1E2025] border-[#E7E0D6] dark:border-white/[0.06]">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-[#1E5A8A]" />
              <span className="text-sm text-[#5B616E] dark:text-white/50">In Review</span>
            </div>
            <p className="text-xl font-bold mt-1 dark:text-white/90">{stats?.reviews || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-[#1E2025] border-[#E7E0D6] dark:border-white/[0.06]">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-[#B3261E]" />
              <span className="text-sm text-[#5B616E] dark:text-white/50">Breaking</span>
            </div>
            <p className="text-xl font-bold mt-1 dark:text-white/90">{stats?.breaking || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-[#1E2025] border-[#E7E0D6] dark:border-white/[0.06]">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#1F7A5A]" />
              <span className="text-sm text-[#5B616E] dark:text-white/50">Total Views</span>
            </div>
            <p className="text-xl font-bold mt-1 dark:text-white/90">{(analytics?.kpis?.total_views || 0).toLocaleString()}</p>
          </CardContent>
        </Card>
      </div>

      {/* Workflow Pipeline (7-stage) */}
      {stats?.pipeline && (
        <Card data-testid="admin-workflow-pipeline" className="bg-white dark:bg-[#1E2025] border-[#E7E0D6] dark:border-white/[0.06]">
          <CardHeader>
            <CardTitle className="text-sm font-medium dark:text-white/90">Editorial Pipeline</CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const stageDefs = [
                { key: 'draft', label: 'Draft', color: '#9A6B1A', extra: [] },
                { key: 'in_review', label: 'In Review', color: '#1E5A8A', extra: ['review'] },
                { key: 'sub_edited', label: 'Sub-Edited', color: '#B89B5E', extra: [] },
                { key: 'legal_review', label: 'Legal', color: '#4A1D2A', extra: [] },
                { key: 'approved', label: 'Approved', color: '#1F7A5A', extra: [] },
                { key: 'scheduled', label: 'Scheduled', color: '#5B616E', extra: [] },
                { key: 'published', label: 'Published', color: '#121316', extra: [] },
              ];
              const p = stats.pipeline || {};
              const values = stageDefs.map(s => (p[s.key] || 0) + s.extra.reduce((a, k) => a + (p[k] || 0), 0));
              const max = Math.max(1, ...values);
              return (
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                  {stageDefs.map((s, i) => (
                    <Link
                      key={s.key}
                      to={`/admin/articles?status=${s.key}`}
                      data-testid={`pipeline-stage-${s.key}`}
                      className="group rounded-xl border border-[#E7E0D6] dark:border-white/[0.06] p-3 hover:border-[#121316] dark:hover:border-white/20 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] uppercase tracking-wider text-[#5B616E] dark:text-white/50">{s.label}</span>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
                      </div>
                      <p className="text-2xl font-bold dark:text-white/90">{values[i].toLocaleString()}</p>
                      <div className="mt-2 h-1 rounded-full bg-[#FBF7EF] dark:bg-white/5 overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${(values[i] / max) * 100}%`, background: s.color }} />
                      </div>
                    </Link>
                  ))}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Articles Trend */}
<Card data-testid="admin-analytics-chart" className="xl:col-span-2 bg-white dark:bg-[#1E2025] border-[#E7E0D6] dark:border-white/[0.06]">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Articles Published (Last 30 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            {analytics?.daily_articles?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={analytics.daily_articles}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(38, 22%, 88%)" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="hsl(210, 55%, 40%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-sm text-[#5B616E]">No data yet</div>
            )}
          </CardContent>
        </Card>

        {/* Articles by Category */}
        <Card className="bg-white dark:bg-[#1E2025] border-[#E7E0D6] dark:border-white/[0.06]">
          <CardHeader>
            <CardTitle className="text-sm font-medium dark:text-white/90">By Category</CardTitle>
          </CardHeader>
          <CardContent>
            {analytics?.articles_by_category?.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={analytics.articles_by_category}
                      dataKey="count"
                      nameKey="category"
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      strokeWidth={2}
                    >
                      {analytics.articles_by_category.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1 mt-2">
                  {analytics.articles_by_category.map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                        <span className="text-[#5B616E]">{item.category}</span>
                      </div>
                      <span className="font-medium">{item.count}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-sm text-[#5B616E]">No data yet</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Realtime Activity Feed */}
      <Card data-testid="admin-realtime-feed" className="bg-white dark:bg-[#1E2025] border-[#E7E0D6] dark:border-white/[0.06]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-2 dark:text-white/90">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#1F7A5A] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#1F7A5A]" />
              </span>
              <Activity className="w-4 h-4 text-[#1F7A5A]" /> Realtime Activity
            </CardTitle>
            <Link to="/admin/audit" className="text-xs text-[#5B616E] hover:text-[#121316] dark:hover:text-white">View all</Link>
          </div>
        </CardHeader>
        <CardContent>
          {activity === null ? (
            <div className="space-y-2">
              {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-9 rounded-md" />)}
            </div>
          ) : activity.length === 0 ? (
            <div className="text-sm text-[#5B616E] py-6 text-center">No activity in the last 24 hours</div>
          ) : (
            <ul className="divide-y divide-[#E7E0D6] dark:divide-white/[0.06]">
              {activity.slice(0, 8).map((log) => {
                const action = (log.action || '').replace(/_/g, ' ');
                const resource = log.resource_type || 'item';
                const who = log.user_name || log.user_email || 'system';
                return (
                  <li key={log.id || log._id} data-testid="admin-activity-item" className="py-2.5 flex items-center gap-3 text-sm">
                    <div className="w-7 h-7 rounded-full bg-[#FBF7EF] dark:bg-white/5 flex items-center justify-center flex-shrink-0">
                      <span className="text-[10px] font-semibold text-[#1E5A8A] uppercase">{(who[0] || '?').toUpperCase()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="truncate dark:text-white/90">
                        <span className="font-medium">{who}</span>{' '}
                        <span className="text-[#5B616E] dark:text-white/50">{action}</span>{' '}
                        <span className="text-[#121316] dark:text-white/80">{resource}</span>
                        {log.resource_id && <span className="text-[#5B616E] dark:text-white/40 text-xs ml-1 font-mono">#{String(log.resource_id).slice(-6)}</span>}
                      </p>
                    </div>
                    <span className="text-xs text-[#5B616E] dark:text-white/40 whitespace-nowrap">{formatActivityTime(log.created_at)}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Scheduled Articles + My Notifications */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Upcoming Scheduled Articles */}
        <Card data-testid="admin-scheduled-widget" className="bg-white dark:bg-[#1E2025] border-[#E7E0D6] dark:border-white/[0.06]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2 dark:text-white/90">
                <Calendar className="w-4 h-4 text-[#1E5A8A]" /> Upcoming Scheduled
              </CardTitle>
              <Link to="/admin/articles?status=scheduled" className="text-xs text-[#5B616E] hover:text-[#121316] dark:hover:text-white">View all</Link>
            </div>
          </CardHeader>
          <CardContent>
            {scheduled === null ? (
              <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-9 rounded-md" />)}</div>
            ) : scheduled.length === 0 ? (
              <div className="text-sm text-[#5B616E] py-6 text-center">No scheduled articles</div>
            ) : (
              <ul className="divide-y divide-[#E7E0D6] dark:divide-white/[0.06]">
                {scheduled.slice(0, 6).map((a) => {
                  const when = a.scheduled_at ? new Date(a.scheduled_at) : null;
                  const diffMs = when ? when.getTime() - Date.now() : 0;
                  const mins = Math.round(diffMs / 60000);
                  const inText = diffMs < 0
                    ? 'overdue'
                    : mins < 60 ? `in ${mins}m`
                    : mins < 60 * 24 ? `in ${Math.round(mins / 60)}h`
                    : `in ${Math.round(mins / 60 / 24)}d`;
                  return (
                    <li key={a.id || a._id} data-testid="admin-scheduled-item" className="py-2.5 flex items-center gap-3 text-sm">
                      <Clock className="w-4 h-4 text-[#5B616E] flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <Link to={`/admin/articles/${a.id || a._id}/edit`} className="block truncate font-medium dark:text-white/90 hover:underline">{a.title || 'Untitled'}</Link>
                        <p className="text-xs text-[#5B616E] dark:text-white/40">{when ? when.toLocaleString() : '—'}</p>
                      </div>
                      <span className={`text-xs whitespace-nowrap ${diffMs < 0 ? 'text-[#B3261E]' : 'text-[#1F7A5A]'}`}>{inText}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* My Notifications */}
        <Card data-testid="admin-notifications-widget" className="bg-white dark:bg-[#1E2025] border-[#E7E0D6] dark:border-white/[0.06]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2 dark:text-white/90">
                <Bell className="w-4 h-4 text-[#9A6B1A]" /> My Notifications
                {notifs?.unread_count > 0 && (
                  <span className="ml-1 text-[10px] bg-[#B3261E] text-white rounded-full px-1.5 py-0.5 font-semibold">{notifs.unread_count}</span>
                )}
              </CardTitle>
              <div className="flex items-center gap-2">
                {notifs?.notifications?.some(n => !n.is_read) && (
                  <Button size="sm" variant="outline" className="text-xs" onClick={async () => {
                    const ids = (notifs?.notifications || []).filter(n => !n.is_read).map(n => n.id || n._id);
                    if (!ids.length) return;
                    try {
                      await notificationsAPI.markRead(ids);
                      const r = await notificationsAPI.list({ limit: 10 });
                      setNotifs(r.data);
                    } catch { toast.error('Failed to mark read'); }
                  }}>Mark all read</Button>
                )}
                <Link to="/admin/notifications" className="text-xs text-[#5B616E] hover:text-[#121316] dark:hover:text-white">View all</Link>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {notifs === null ? (
              <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-9 rounded-md" />)}</div>
            ) : (notifs.notifications || []).length === 0 ? (
              <div className="text-sm text-[#5B616E] py-6 text-center">You're all caught up ✓</div>
            ) : (
              <ul className="divide-y divide-[#E7E0D6] dark:divide-white/[0.06]">
                {(notifs.notifications || []).slice(0, 6).map((n) => (
                  <li key={n.id || n._id} data-testid="admin-notification-item" className={`py-2.5 flex items-start gap-3 text-sm ${!n.is_read ? 'bg-[#FBF7EF]/50 dark:bg-white/[0.02] -mx-2 px-2 rounded-md' : ''}`}>
                    <span className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${!n.is_read ? 'bg-[#B3261E]' : 'bg-[#E7E0D6]'}`} />
                    <div className="flex-1 min-w-0">
                      {n.link ? (
                        <Link to={n.link} className="block truncate font-medium dark:text-white/90 hover:underline">{n.title || 'Notification'}</Link>
                      ) : (
                        <p className="truncate font-medium dark:text-white/90">{n.title || 'Notification'}</p>
                      )}
                      {n.message && <p className="text-xs text-[#5B616E] dark:text-white/40 truncate">{n.message}</p>}
                    </div>
                    <span className="text-xs text-[#5B616E] dark:text-white/40 whitespace-nowrap">{formatActivityTime(n.created_at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Revenue / Engagement KPI Bar Cards */}
      <KPIBarGrid items={[
        {
          title: 'Total Views',
          value: (analytics?.kpis?.total_views || 0).toLocaleString(),
          change: '+12%',
          changeType: 'positive',
          chartData: (analytics?.daily_articles || []).slice(-7).map((d, i) => ({
            weekday: ['Mo','Tu','We','Th','Fr','Sa','Su'][i % 7],
            value: d.count * 120 + Math.floor(Math.random() * 500),
          })),
        },
        {
          title: 'Subscribers',
          value: (stats?.subscribers || 0).toLocaleString(),
          change: '+3%',
          changeType: 'positive',
          chartData: (analytics?.daily_articles || []).slice(-7).map((d, i) => ({
            weekday: ['Mo','Tu','We','Th','Fr','Sa','Su'][i % 7],
            value: Math.floor(d.count * 15 + Math.random() * 40),
          })),
        },
        {
          title: 'Engagement',
          value: (stats?.comments || 0).toLocaleString(),
          change: '-2%',
          changeType: 'negative',
          chartData: (analytics?.daily_articles || []).slice(-7).map((d, i) => ({
            weekday: ['Mo','Tu','We','Th','Fr','Sa','Su'][i % 7],
            value: Math.floor(d.count * 8 + Math.random() * 100),
          })),
        },
      ]} />

      {/* Circle Chart KPIs */}
      <CircleChartGrid items={[
        { title: 'Published', color: 'primary', total: stats?.articles || 1, chartData: [{ name: 'Published', value: stats?.published || 0 }] },
        { title: 'Comments', color: 'success', total: Math.max(stats?.comments || 1, stats?.comments || 0), chartData: [{ name: 'Active', value: Math.floor((stats?.comments || 0) * 0.75) }] },
        { title: 'Subscribers', color: 'warning', total: Math.max(stats?.subscribers || 1, 100), chartData: [{ name: 'Newsletter', value: stats?.subscribers || 0 }] },
        { title: 'Drafts', color: 'danger', total: stats?.articles || 1, chartData: [{ name: 'Drafts', value: stats?.drafts || 0 }] },
      ]} />

      {/* Area Trend Cards */}
      <AreaTrendGrid items={[
        {
          title: 'New Articles',
          value: stats?.articles || 0,
          change: '+15%',
          changeType: 'positive',
          xaxis: 'month',
          chartData: (analytics?.daily_articles || []).map(d => ({ month: d.date, value: d.count })),
        },
        {
          title: 'Active Users',
          value: stats?.users || 0,
          change: '0%',
          changeType: 'neutral',
          xaxis: 'month',
          chartData: (analytics?.daily_articles || []).map((d, i) => ({ month: d.date, value: Math.floor((stats?.users || 10) * (0.8 + Math.sin(i / 3) * 0.2)) })),
        },
        {
          title: 'Comments',
          value: (stats?.comments || 0).toLocaleString(),
          change: '-5%',
          changeType: 'negative',
          xaxis: 'month',
          chartData: (analytics?.daily_articles || []).map((d, i) => ({ month: d.date, value: Math.floor((stats?.comments || 5) / 30 * (0.7 + Math.cos(i / 4) * 0.3)) })),
        },
      ]} />

      {/* Colored Area Insight Cards */}
      <ColoredAreaGrid items={[
        {
          title: 'Bounce Rate',
          value: '42.3%',
          change: '-5.9%',
          color: 'default',
          icon: Gauge,
          xaxis: 'month',
          chartData: [{ month: 'Jan', value: 18 }, { month: 'Feb', value: 22 }, { month: 'Mar', value: 38 }, { month: 'Apr', value: 54 }, { month: 'May', value: 66 }, { month: 'Jun', value: 71 }, { month: 'Jul', value: 63 }, { month: 'Aug', value: 52 }],
        },
        {
          title: 'Avg. Session Duration',
          value: '4:32',
          change: '+12.4%',
          color: 'success',
          icon: Timer,
          xaxis: 'month',
          chartData: [{ month: 'Jan', value: 205 }, { month: 'Feb', value: 185 }, { month: 'Mar', value: 242 }, { month: 'Apr', value: 228 }, { month: 'May', value: 265 }, { month: 'Jun', value: 245 }, { month: 'Jul', value: 262 }, { month: 'Aug', value: 272 }],
        },
        {
          title: 'Conversion Rate',
          value: '3.8%',
          change: '+1.7%',
          color: 'warning',
          icon: MousePointerClick,
          xaxis: 'month',
          chartData: [{ month: 'Jan', value: 2.1 }, { month: 'Feb', value: 2.4 }, { month: 'Mar', value: 3.2 }, { month: 'Apr', value: 2.8 }, { month: 'May', value: 3.5 }, { month: 'Jun', value: 3.2 }, { month: 'Jul', value: 3.6 }, { month: 'Aug', value: 3.8 }],
        },
        {
          title: 'Pages per Session',
          value: '4.2',
          change: '+4.20',
          color: 'primary',
          icon: Globe,
          xaxis: 'day',
          chartData: [{ month: 'Mon', value: 3.8 }, { month: 'Tue', value: 4.5 }, { month: 'Wed', value: 4.1 }, { month: 'Thu', value: 3.9 }, { month: 'Fri', value: 4.3 }, { month: 'Sat', value: 4.0 }, { month: 'Sun', value: 4.2 }],
        },
        {
          title: 'New vs Returning',
          value: '48.5%',
          change: '-12.9%',
          color: 'secondary',
          icon: UserCheck,
          xaxis: 'month',
          chartData: [{ month: 'Jan', value: 72 }, { month: 'Feb', value: 74 }, { month: 'Mar', value: 72 }, { month: 'Apr', value: 70 }, { month: 'May', value: 70 }, { month: 'Jun', value: 69 }, { month: 'Jul', value: 68 }, { month: 'Aug', value: 69 }],
        },
        {
          title: 'Load Time',
          value: '856ms',
          change: '-28.7%',
          color: 'danger',
          icon: ServerCrash,
          xaxis: 'month',
          chartData: [{ month: 'Jan', value: 200 }, { month: 'Feb', value: 380 }, { month: 'Mar', value: 250 }, { month: 'Apr', value: 390 }, { month: 'May', value: 420 }, { month: 'Jun', value: 485 }, { month: 'Jul', value: 520 }, { month: 'Aug', value: 516 }],
        },
      ]} />

      {/* Top Articles */}
      {analytics?.top_articles?.length > 0 && (
        <Card className="bg-white dark:bg-[#1E2025] border-[#E7E0D6] dark:border-white/[0.06]">
          <CardHeader>
            <CardTitle className="text-sm font-medium dark:text-white/90">Top Articles by Views</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.top_articles.slice(0, 5).map((article, idx) => (
                <div key={article.id} className="flex items-center gap-4">
                  <span className="text-lg font-bold text-[#E7E0D6] w-8 text-right news-masthead">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{article.title}</p>
                    <p className="text-xs text-[#5B616E]">{(article.views || 0).toLocaleString()} views</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Seed Status Card */}
      {seedStatus && (
        <Card className="bg-white dark:bg-[#1E2025] border-[#E7E0D6] dark:border-white/[0.06]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2 dark:text-white/90">
                <Database className="w-4 h-4 text-[#B89B5E]" /> Demo Data Seed
              </CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="text-xs" onClick={async () => {
                  setSeeding(true);
                  try {
                    const r = await seedAPI.run(false);
                    toast.success(`Seed complete in ${r.data.elapsed_s}s`);
                    seedAPI.status().then(r2 => setSeedStatus(r2.data)).catch(() => {});
                  } catch { toast.error('Seed failed'); }
                  finally { setSeeding(false); }
                }} disabled={seeding}>
                  <RefreshCw className={`w-3 h-3 mr-1 ${seeding ? 'animate-spin' : ''}`} /> {seeding ? 'Seeding…' : 'Reseed'}
                </Button>
                <Button size="sm" variant="outline" className="text-xs text-red-600 border-red-200" onClick={async () => {
                  if (!window.confirm('Force reseed will clear ALL data. Are you sure?')) return;
                  setSeeding(true);
                  try {
                    const r = await seedAPI.run(true);
                    toast.success(`Force seed complete in ${r.data.elapsed_s}s`);
                    seedAPI.status().then(r2 => setSeedStatus(r2.data)).catch(() => {});
                  } catch { toast.error('Force seed failed'); }
                  finally { setSeeding(false); }
                }} disabled={seeding}>
                  Force Reset
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 mb-3">
              <span className={`w-2 h-2 rounded-full ${seedStatus.is_seeded ? 'bg-green-500' : 'bg-amber-500'}`} />
              <span className="text-sm">{seedStatus.is_seeded ? '✅ Fully seeded' : '⚠️ Needs seeding'}</span>
              <span className="text-xs text-[#5B616E] ml-auto">{seedStatus.total_documents?.toLocaleString()} documents</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(seedStatus.counts || {}).slice(0, 12).map(([k, v]) => (
                <div key={k} className="flex justify-between text-[10px] text-[#5B616E]">
                  <span className="capitalize">{k.replace(/_/g, ' ')}</span>
                  <span className="font-medium text-[#121316]">{v}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

        {/* SLA + Content Expiry panels */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Workflow SLA */}
        <Card className="bg-white dark:bg-[#1E2025] border-[#E7E0D6] dark:border-white/[0.06]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2 dark:text-white/90">
                <Clock className="w-4 h-4 text-amber-500" /> Workflow SLA (24h)
              </CardTitle>
              <Button size="sm" variant="outline" onClick={handleEscalate} disabled={escalating} className="text-xs">
                {escalating ? 'Sending…' : 'Escalate all'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {slaData ? (
              slaData.overdue_count === 0 ? (
                <p className="text-sm text-green-600">✓ No overdue articles</p>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-amber-600 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> {slaData.overdue_count} articles overdue in review</p>
                  {slaData.overdue_articles?.slice(0, 4).map(a => (
                    <div key={a.id} className="flex items-center justify-between text-xs">
                      <p className="truncate flex-1 text-[#121316]">{a.title}</p>
                      <span className="ml-2 text-[#B3261E] whitespace-nowrap">{a.status}</span>
                    </div>
                  ))}
                  {slaData.overdue_count > 4 && <p className="text-xs text-[#5B616E]">…and {slaData.overdue_count - 4} more</p>}
                </div>
              )
            ) : <p className="text-xs text-[#5B616E]">Loading…</p>}
          </CardContent>
        </Card>

        {/* Content Expiry */}
        <Card className="bg-white dark:bg-[#1E2025] border-[#E7E0D6] dark:border-white/[0.06]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2 dark:text-white/90">
                <Archive className="w-4 h-4 text-[#5B616E]" /> Content Expiry
              </CardTitle>
              {expiryData?.count > 0 && (
                <Button size="sm" variant="outline" onClick={handleArchiveExpired} disabled={archiving} className="text-xs">
                  {archiving ? 'Archiving…' : 'Archive all'}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {expiryData ? (
              expiryData.count === 0 ? (
                <p className="text-sm text-green-600">✓ No expired articles</p>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-[#B3261E]">{expiryData.count} published articles have passed their expiry date</p>
                  {expiryData.expiring?.slice(0, 4).map(a => (
                    <div key={a.id} className="flex items-center justify-between text-xs">
                      <p className="truncate flex-1 text-[#121316]">{a.title}</p>
                      <span className="ml-2 text-[#5B616E] whitespace-nowrap">{a.expires_at ? new Date(a.expires_at).toLocaleDateString() : ''}</span>
                    </div>
                  ))}
                </div>
              )
            ) : <p className="text-xs text-[#5B616E]">Loading…</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
